// src/views/Home.vue
<script setup>
import { ref, onMounted, watch, nextTick } from 'vue';
import { supabase } from '../../supabase.js';
import { Modal } from 'bootstrap'; // Import Modal class from Bootstrap JS

const props = defineProps({
  session: { type: Object, default: null }
});

const dossiersList = ref([]);
const loadingList = ref(false);
const fetchError = ref('');

const selectedDossierFull = ref(null);
const selectedDossierEvolutions = ref([]);
const loadingEvolutions = ref(false);
const evolutionError = ref('');
const newEvolutionNote = ref('');
const addingEvolution = ref(false);
const deleteConfirmModalElement = ref(null); // Ref for delete modal
let deleteConfirmModalInstance = null; // Instance for delete modal

// --- Delete State ---
const dossierToDelete = ref(null); // Store the dossier object to be deleted
const deletingDossier = ref(false);
const deleteError = ref('');
// --- MODAL State & Instance ---
const detailModalRef = ref(null); // Template ref for the modal DOM element
let detailModalInstance = null; // To hold the Bootstrap Modal JS instance

// --- Data Fetching (fetchDossiers, fetchEvolutions remain the same) ---
const fetchDossiers = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) { dossiersList.value = []; fetchError.value = 'Non connecté.'; loadingList.value = false; return; }
    loadingList.value = true; fetchError.value = '';
    try {
        const { data, error } = await supabase.functions.invoke('get-dossiers');
        if (error) throw error;
        dossiersList.value = Array.isArray(data) ? data : [];
    } catch (err) { /* ... error handling ... */ fetchError.value = `Err chargement: ${err.message || err}`; dossiersList.value = []; }
    finally { loadingList.value = false; }
};

const fetchEvolutions = async (dossierId) => {
    if (!dossierId) return;
    loadingEvolutions.value = true; evolutionError.value = ''; selectedDossierEvolutions.value = [];
    try {
        const { data, error } = await supabase.functions.invoke(`get-evolutions?dossierId=${dossierId}`);
        if (error) throw error;
        selectedDossierEvolutions.value = data || [];
    } catch (err) { /* ... error handling ... */ evolutionError.value = `Err évolutions: ${err.message || err}`; }
    finally { loadingEvolutions.value = false; }
};

// --- Event Handlers & Actions ---

// ** MODIFIED to handle Bootstrap Modal **
const showDossierDetails = (dossier) => {
    selectedDossierFull.value = dossier; // Set the data
    fetchEvolutions(dossier.id);      // Fetch associated data
    if (detailModalInstance) {
        detailModalInstance.show();      // Show the modal using Bootstrap JS API
    } else {
        console.error("Modal instance not available."); // Should have been initialized in onMounted
    }
};

// ** MODIFIED to potentially hide modal if needed (though data-bs-dismiss should work) **
const closeDossierDetails = () => {
    // Bootstrap's data-bs-dismiss usually handles hiding,
    // but we reset the state here regardless
    selectedDossierFull.value = null;
    selectedDossierEvolutions.value = [];
    evolutionError.value = '';
    newEvolutionNote.value = '';
    // Optionally force hide if data-bs-dismiss isn't working reliably
    // detailModalInstance?.hide();
};

const addEvolution = async () => {
    if (!selectedDossierFull.value || !newEvolutionNote.value.trim()) return;
    addingEvolution.value = true; evolutionError.value = '';
    const payload = { dossier_id: selectedDossierFull.value.id, note: newEvolutionNote.value.trim() };
    try {
        const { data: newEvo, error } = await supabase.functions.invoke('add-evolution', { body: payload });
        if (error) throw error;
        selectedDossierEvolutions.value.unshift(newEvo); // Add to top
        newEvolutionNote.value = '';
    } catch (err) { /* ... error handling ... */ evolutionError.value = `Err ajout évolution: ${err.message || err}`; }
    finally { addingEvolution.value = false; }
};
const copyModalDossierText = () => {
    if (selectedDossierFull.value && selectedDossierFull.value.dossier_text) {
        navigator.clipboard.writeText(selectedDossierFull.value.dossier_text).then(() => {
            alert('Texte du dossier copié !');
        }).catch(err => {
            console.error('Erreur copie modal: ', err);
            alert('Erreur copie.');
        });
    } else {
        console.warn("Attempted to copy null or empty dossier text from modal.");
        alert("Contenu du dossier non disponible pour la copie.");
    }
};
// --- NEW Delete Logic ---
const confirmDeleteDossier = (dossier) => {
    console.log("Requesting delete confirmation for:", dossier.id);
    dossierToDelete.value = dossier; // Store the dossier to delete
    deleteError.value = ''; // Clear previous errors
    deleteConfirmModalInstance?.show(); // Show the confirmation modal
};

const executeDelete = async () => {
    if (!dossierToDelete.value) return;

    deletingDossier.value = true;
    deleteError.value = '';
    const idToDelete = dossierToDelete.value.id;
    console.log("Executing delete for:", idToDelete);

    try {
        // Call the Edge Function using DELETE method
        const { error } = await supabase.functions.invoke(
            `delete-dossier/${idToDelete}`, // Pass ID in URL path
            { method: 'DELETE' }
        );

        if (error) throw error;

        // Success!
        console.log(`Dossier ${idToDelete} deleted.`);
        // Remove from local list
        dossiersList.value = dossiersList.value.filter(d => d.id !== idToDelete);
        // Close the confirmation modal
        deleteConfirmModalInstance?.hide();
        dossierToDelete.value = null; // Clear selection

        // If the deleted dossier was the one being detailed, close that too
        if(selectedDossierFull.value && selectedDossierFull.value.id === idToDelete) {
            closeDossierDetails();
        }

    } catch (err) {
        console.error("Error deleting dossier:", err);
        let msg = err.message;
        if (err.context?.json?.error) msg = err.context.json.error;
        deleteError.value = `Erreur suppression: ${msg}`;
        // Keep modal open to show error
    } finally {
        deletingDossier.value = false;
    }
};

// --- Utility ---
const formatDate = (dateString, options = { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) => {
    if (!dateString) {
        // console.warn("formatDate received null or empty dateString");
        return 'Date N/A'; // Return a clear indicator if date is missing
    }
    try {
        const date = new Date(dateString);
        // Check if the date object is valid after parsing
        if (isNaN(date.getTime())) {
             console.warn("formatDate could not parse dateString:", dateString);
            return 'Date Invalide';
        }
        // Use Intl.DateTimeFormat for locale-aware formatting
        return new Intl.DateTimeFormat('fr-FR', options).format(date);
    } catch (e) {
        console.error("Error formatting date:", dateString, e);
        return 'Erreur Date'; // Return error indicator
    }
};
// --- Lifecycle & Watchers ---
watch(() => props.session, (newSession, oldSession) => {
    if (!!newSession !== !!oldSession || (newSession && oldSession === undefined)) { fetchDossiers(); }
    else if (!newSession) { dossiersList.value = []; fetchError.value = 'Veuillez vous connecter.'; }
}, { immediate: true });

// ** MODIFIED onMounted to initialize Modal **
onMounted(() => {
  // Initialize the Bootstrap Modal instance after the component is mounted
  if (detailModalRef.value) {
      detailModalInstance = new Modal(detailModalRef.value);

      // Optional: Add event listener if you need to cleanup state when modal is hidden via other means (e.g., backdrop click)
      detailModalRef.value.addEventListener('hidden.bs.modal', () => {
          console.log("Modal hidden event triggered");
          // We call closeDossierDetails to reset state even if hidden by backdrop/esc key
          closeDossierDetails();
      });
  } else {
      console.warn("Template ref 'detailModalRef' not found on mount.");
  }
   // Initialize Delete Confirmation Modal
   if (deleteConfirmModalElement.value) {
      deleteConfirmModalInstance = new Modal(deleteConfirmModalElement.value);
       // Optional: Clear error when delete modal is hidden
      deleteConfirmModalElement.value.addEventListener('hidden.bs.modal', () => {
           dossierToDelete.value = null; // Clear selection when modal closes
           deleteError.value = '';
      });
  } else { console.warn("Delete confirm modal element not found."); }

  // Initial fetch is handled by the watcher now
});

</script>

<template>
  <div class="home-view container mt-3">
    <h1 class="mb-4">Liste des Dossiers Médicaux</h1>

    <!-- List Section -->
    <section class="patient-list-section mb-4">
        <!-- ... Loading/Error/Empty states ... -->
        <div v-if="loadingList" class="d-flex justify-content-center my-4"> <!-- Loading Spinner -->
            <div class="spinner-border text-primary" role="status"><span class="visually-hidden">Chargement...</span></div>
        </div>
        <div v-else-if="fetchError" class="alert alert-danger" role="alert">Erreur: {{ fetchError }}</div>
        <div v-else-if="!dossiersList || dossiersList.length === 0" class="text-center text-muted p-4 border rounded bg-light">Aucun dossier enregistré.</div>

        <!-- Dossier List using Bootstrap List Group -->
        <div v-else class="list-group">
            <!-- Use <a> tags for list items, prevent default navigation -->
            <a href="#" v-for="dossier in dossiersList" :key="dossier.id"
                class="list-group-item list-group-item-action flex-column align-items-start mb-2 shadow-sm"
                @click.prevent="showDossierDetails(dossier)"
                :class="{ 'active': selectedDossierFull && selectedDossierFull.id === dossier.id }">
                <div class="d-flex w-100 justify-content-between">
                    <h5 class="mb-1">{{ dossier.patient_nom || 'Nom Inconnu' }} <small class="text-muted fw-normal">({{ dossier.patient_age || '?' }}a)</small></h5>
                    <small class="text-muted">{{ formatDate(dossier.created_at) }}</small>
                </div>
                <p class="mb-1 small text-muted">Motif: {{ dossier.motif_consultation || 'Non spécifié' }}</p>
                <div class="mt-2 text-end list-item-actions">
                    <!-- Pass dossier object to show details -->
                    <!-- <button @click.stop="showDossierDetails(dossier)" class="btn btn-sm btn-outline-info me-2" title="Voir détails">Détails</button> -->
                    <!-- Edit button navigates -->
                    <router-link :to="{ name: 'edit-dossier', params: { id: dossier.id } }" @click.stop class="btn btn-sm btn-outline-warning" tag="button" title="Modifier ce dossier">
                        Modifier
                    </router-link>
                    <button @click.stop="confirmDeleteDossier(dossier)" class="btn btn-sm btn-outline-danger" title="Supprimer ce dossier">
                     Supprimer
                 </button>
                </div>
            </a>
        </div>
    </section>

     <!-- Detail & Evolution Modal -->
     <!-- Add ref="detailModalRef" to the modal div -->
     <div class="modal fade" id="dossierDetailModal" tabindex="-1" aria-labelledby="dossierDetailModalLabel" aria-hidden="true" ref="detailModalRef">
       <div class="modal-dialog modal-lg modal-dialog-scrollable">
         <div class="modal-content">
           <div class="modal-header">
             <h5 class="modal-title" id="dossierDetailModalLabel">Détails & Évolution: {{ selectedDossierFull?.patient_nom }}</h5>
             <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button> <!-- Bootstrap handles closing -->
           </div>
           <div class="modal-body">
                <!-- Use v-if to ensure selectedDossierFull exists before accessing properties -->
                <div v-if="selectedDossierFull">
                    <h6>Dossier Initial</h6>
                    <!-- Added Copy button inside modal -->
                    <div class="d-flex justify-content-end mb-2">
                         <button @click="copyModalDossierText" class="btn btn-sm btn-outline-secondary" title="Copier le texte du dossier">
                              Copier Texte
                         </button>
                    </div>
                    <pre class="output dossier-display bg-light p-2 border rounded mb-3">{{ selectedDossierFull.dossier_text }}</pre>
                    <hr class="my-3">
                    <h6>Évolutions</h6>
                    <div class="add-evolution-form mb-3 p-3 bg-light border rounded">
                        <label :for="'evo-note-modal-' + selectedDossierFull.id" class="form-label small">Ajouter une note:</label>
                        <textarea class="form-control form-control-sm" :id="'evo-note-modal-' + selectedDossierFull.id" v-model="newEvolutionNote" placeholder="..." rows="3"></textarea>
                        <button @click="addEvolution" class="btn btn-sm btn-success mt-2" :disabled="addingEvolution || !newEvolutionNote.trim()">
                            {{ addingEvolution ? 'Ajout...' : '+ Ajouter' }}
                        </button>
                        <p v-if="evolutionError" class="alert alert-danger alert-sm small mt-2 p-1">{{ evolutionError }}</p>
                    </div>
                    <div v-if="loadingEvolutions" class="text-center my-3"><div class="spinner-border spinner-border-sm" role="status"><span class="visually-hidden">...</span></div></div>
                    <ul v-else-if="selectedDossierEvolutions.length > 0" class="evolution-list list-unstyled">
                        <li v-for="evo in selectedDossierEvolutions" :key="evo.id" class="evolution-item mb-2 p-2 border rounded">
                            <p class="evolution-note mb-1">{{ evo.note }}</p>
                            <small class="evolution-meta d-block text-end text-muted">Le {{ formatDate(evo.evolution_date || evo.created_at, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) }}</small>
                        </li>
                    </ul>
                    <p v-else-if="!evolutionError" class="text-center text-muted small mt-3">Aucune note d'évolution.</p>
                 </div>
                 <div v-else class="text-center p-4 text-muted">
                      Chargement des détails...
                 </div>
           </div>
           <div class="modal-footer">
              <!-- data-bs-dismiss handles closing -->
             <button type="button" class="btn btn-secondary btn-sm" data-bs-dismiss="modal">Fermer</button>
           </div>
         </div>
       </div>
     </div>
     <!-- End Modal -->
       <!-- *** NEW DELETE CONFIRMATION MODAL *** -->
       <div class="modal fade" id="deleteConfirmModal" tabindex="-1" aria-labelledby="deleteConfirmModalLabel" aria-hidden="true" ref="deleteConfirmModalElement">
        <div class="modal-dialog">
          <div class="modal-content">
            <div class="modal-header bg-danger text-white">
              <h5 class="modal-title" id="deleteConfirmModalLabel">Confirmer Suppression</h5>
              <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div class="modal-body">
              Êtes-vous sûr de vouloir supprimer définitivement le dossier de
              <strong>{{ dossierToDelete?.patient_nom || 'ce patient' }}</strong> ?
              <br>
              <small class="text-danger">Cette action est irréversible.</small>
               <p v-if="deleteError" class="error-message small mt-2">{{ deleteError }}</p>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary btn-sm" data-bs-dismiss="modal">Annuler</button>
              <button type="button" class="btn btn-danger btn-sm" @click="executeDelete" :disabled="deletingDossier">
                {{ deletingDossier ? 'Suppression...' : 'Oui, Supprimer' }}
              </button>
            </div>
          </div>
        </div>
      </div>
     <!-- *** END DELETE MODAL *** -->

  </div>
</template>